import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import DashboardKpiCard, { DashboardKpiRow } from "./DashboardKpiCard";

import { useI18n } from "../i18n/I18nContext";
import { GAIN_MASSIQUE_POP_LABELS, GAIN_MASSIQUE_POP_OPTIONS } from "../utils/empilementPopulations";

const COLORS = {
  melange: "#5B9BD5",
  "15mm": "#5B9BD5",
  "22mm": "#F58518",
  "30mm": "#54A24B",
  "32mm": "#B279A2",
  yellow: "#E6FF3E",
  gray: "#94a3b8",
  red: "#f87171",
  grid: "#2a4a62",
  white: "#e2e8f0",
};

const MONTHS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];

const GRAN_LABELS = { jour: "Jour", semaine: "Semaine", mois: "Mois" };
const POP_LABELS = {
  melange: "toutes populations",
  ...GAIN_MASSIQUE_POP_LABELS,
};

export function formatGainPeriod(period, gran) {
  if (!period) return "—";
  if (gran === "jour") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  }
  if (gran === "semaine") {
    const m = /^(\d{4})-S(\d{2})$/.exec(period);
    if (m) return `Sem. ${m[2]} · ${m[1]}`;
  }
  if (gran === "mois") {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    if (m) {
      const mi = parseInt(m[2], 10) - 1;
      return `${MONTHS_FR[mi] ?? m[2]} ${m[1]}`;
    }
  }
  return period;
}

function quantile(sorted, p) {
  if (!sorted.length) return 0;
  const pos = (sorted.length - 1) * p;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

function boxStats(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const median = quantile(sorted, 0.5);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;
  const whiskerLow = sorted.find((v) => v >= q1 - 1.5 * iqr) ?? sorted[0];
  const whiskerHigh = [...sorted].reverse().find((v) => v <= q3 + 1.5 * iqr) ?? sorted[sorted.length - 1];
  const mean = sorted.reduce((a, b) => a + b, 0) / sorted.length;
  return { q1, median, q3, whiskerLow, whiskerHigh, mean, points: sorted };
}

function getOutlierPoints(points, stats) {
  if (!stats || !points.length) return [];
  return points.filter(
    (p) => p.gain < stats.whiskerLow || p.gain > stats.whiskerHigh
  );
}

function mergePopData(byPop, popMode, periods) {
  const pops = popMode === "melange" ? Object.keys(byPop || {}) : [popMode];
  const merged = {};
  pops.forEach((pop) => {
    const m = byPop?.[pop];
    if (!m) return;
    Object.keys(m).forEach((p) => {
      merged[p] = (merged[p] || []).concat(m[p]);
    });
  });
  return periods.map((p) => ({
    period: p,
    points: (merged[p] || []).map(([stackId, gain]) => ({ stackId, gain })),
  }));
}

function BoxplotSvg({ series, color, width, height, gran }) {
  const margin = { top: 32, right: 28, bottom: 110, left: 72 };
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;

  const boxes = useMemo(() => {
    return series.map((s) => ({
      ...s,
      label: formatGainPeriod(s.period, gran),
      stats: boxStats(s.points.map((p) => p.gain)),
    }));
  }, [series, gran]);

  const allY = boxes.flatMap((b) => b.points.map((p) => p.gain));
  const yMin = allY.length ? Math.min(...allY) : 0;
  const yMax = allY.length ? Math.max(...allY) : 100;
  const pad = (yMax - yMin) * 0.1 || 1;
  const domainMin = Math.max(0, yMin - pad);
  const domainMax = yMax + pad;

  const yScale = (v) =>
    margin.top + innerH - ((v - domainMin) / (domainMax - domainMin)) * innerH;

  const n = Math.max(series.length, 1);
  const slotW = innerW / n;
  const boxW = Math.min(80, Math.max(44, slotW * 0.5));

  const yTicks = 6;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    domainMin + ((domainMax - domainMin) * i) / yTicks
  );

  return (
    <svg width={width} height={height} role="img" aria-label="Boxplot gain massique">
      {tickValues.map((v) => (
        <g key={v}>
          <line
            x1={margin.left}
            x2={margin.left + innerW}
            y1={yScale(v)}
            y2={yScale(v)}
            stroke={COLORS.grid}
            strokeWidth={1}
            strokeDasharray="4 4"
          />
          <text
            x={margin.left - 10}
            y={yScale(v) + 5}
            textAnchor="end"
            fill={COLORS.white}
            fontSize={13}
            fontWeight={600}
          >
            {v.toFixed(1)} %
          </text>
        </g>
      ))}

      {boxes.map((b, i) => {
        const cx = margin.left + slotW * i + slotW / 2;
        const st = b.stats;
        if (!st) return null;

        const outliers = getOutlierPoints(b.points, st);

        const jitter = (idx) => {
          const seed = ((idx * 17 + i * 31) % 100) / 100;
          return (seed - 0.5) * boxW * 0.85;
        };

        return (
          <g key={b.period}>
            <line
              x1={cx}
              x2={cx}
              y1={yScale(st.whiskerHigh)}
              y2={yScale(st.q3)}
              stroke={color}
              strokeWidth={3}
            />
            <line
              x1={cx}
              x2={cx}
              y1={yScale(st.q1)}
              y2={yScale(st.whiskerLow)}
              stroke={color}
              strokeWidth={3}
            />
            <line
              x1={cx - boxW / 3}
              x2={cx + boxW / 3}
              y1={yScale(st.whiskerHigh)}
              y2={yScale(st.whiskerHigh)}
              stroke={color}
              strokeWidth={3}
              strokeLinecap="round"
            />
            <line
              x1={cx - boxW / 3}
              x2={cx + boxW / 3}
              y1={yScale(st.whiskerLow)}
              y2={yScale(st.whiskerLow)}
              stroke={color}
              strokeWidth={3}
              strokeLinecap="round"
            />
            <rect
              x={cx - boxW / 2}
              y={yScale(st.q3)}
              width={boxW}
              height={Math.max(2, yScale(st.q1) - yScale(st.q3))}
              fill={color}
              fillOpacity={0.35}
              stroke={color}
              strokeWidth={2.5}
              rx={3}
            />
            <line
              x1={cx - boxW / 2}
              x2={cx + boxW / 2}
              y1={yScale(st.median)}
              y2={yScale(st.median)}
              stroke="#fff"
              strokeWidth={3.5}
            />
            <line
              x1={cx - boxW / 2}
              x2={cx + boxW / 2}
              y1={yScale(st.mean)}
              y2={yScale(st.mean)}
              stroke={COLORS.yellow}
              strokeWidth={2.5}
              strokeDasharray="6 4"
            />
            {outliers.map((p, idx) => (
              <circle
                key={`${p.stackId}-${idx}`}
                cx={cx + jitter(idx)}
                cy={yScale(p.gain)}
                r={4}
                fill={COLORS.red}
                fillOpacity={0.9}
                stroke="#fff"
                strokeWidth={1}
              >
                <title>{`Outlier — Stack ${p.stackId}\n${b.label}\nGain ${p.gain.toFixed(4)} %`}</title>
              </circle>
            ))}
            <text
              x={cx}
              y={height - margin.bottom + 28}
              textAnchor="end"
              fill={COLORS.white}
              fontSize={13}
              fontWeight={600}
              transform={`rotate(-35, ${cx}, ${height - margin.bottom + 28})`}
            >
              {b.label}
            </text>
            <text
              x={cx}
              y={margin.top - 10}
              textAnchor="middle"
              fill={COLORS.gray}
              fontSize={11}
              fontWeight={700}
            >
              n={b.points.length}{outliers.length > 0 ? ` · ${outliers.length} out.` : ""}
            </text>
          </g>
        );
      })}

      <text
        x={22}
        y={margin.top + innerH / 2}
        textAnchor="middle"
        fill={COLORS.white}
        fontSize={14}
        fontWeight={700}
        transform={`rotate(-90, 22, ${margin.top + innerH / 2})`}
      >
        Gain massique moyen / stack (%)
      </text>
    </svg>
  );
}

export default function GainMassiqueBoxplot({ active = true }) {
  const { t, ui } = useI18n();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [gran, setGran] = useState("mois");
  const [popMode, setPopMode] = useState("melange");
  const [chartWidth, setChartWidth] = useState(900);

  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/dashboard/gain-massique");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status === "error") throw new Error(json.message || "Erreur API");
        if (!cancelled) {
          setPayload(json);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur chargement gain massique");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, loaded]);

  useEffect(() => {
    const onResize = () => {
      const el = document.getElementById("gain-massique-chart-wrap");
      if (el) setChartWidth(Math.max(400, el.clientWidth));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [active, loaded]);

  const { series, nPoints, gains } = useMemo(() => {
    const block = payload?.data?.[gran];
    if (!block) return { series: [], nPoints: 0, gains: [] };
    const merged = mergePopData(block.by_pop, popMode, block.periods).map((s) => ({
      ...s,
      label: formatGainPeriod(s.period, gran),
    }));
    const allGains = merged.flatMap((s) => s.points.map((p) => p.gain));
    const n = allGains.length;
    return { series: merged, nPoints: n, gains: allGains };
  }, [payload, gran, popMode]);

  const summary = payload?.summary;
  const color = COLORS[popMode] || COLORS.melange;

  if (!active) return null;

  const gainMoy = gains.length
    ? (gains.reduce((a, b) => a + b, 0) / gains.length).toFixed(4)
    : "—";

  return (
    <>
      {loading && (
        <div style={{ color: COLORS.yellow, padding: 20, textAlign: "center" }}>Chargement gain massique…</div>
      )}
      {error && (
        <div style={{ color: COLORS.red, padding: 20, textAlign: "center" }}>{error}</div>
      )}

      {loaded && !error && (
        <>
          <DashboardKpiRow>
            <DashboardKpiCard
              label="Stacks retenus"
              value={payload?.n_records ?? 0}
              sub="gain massique valide"
              color={COLORS.melange}
            />
            <DashboardKpiCard
              label="Gain moyen"
              value={summary?.gain_moyen != null ? `${Number(summary.gain_moyen).toFixed(4)} %` : `${gainMoy} %`}
              sub={`${nPoints} stack(s) · ${POP_LABELS[popMode]}`}
              color={COLORS.yellow}
            />
            <DashboardKpiCard
              label="Stacks exclus"
              value={payload?.stats?.no_gain ?? 0}
              sub="sans gain valide"
              color={COLORS.red}
            />
            <DashboardKpiCard
              label="Périodes"
              value={series.length}
              sub={GRAN_LABELS[gran]}
              color="#a78bfa"
            />
          </DashboardKpiRow>

          <div style={S.card}>
            <div style={S.headerRow}>
              <div>
                <div style={S.cardTitle}>Boxplot — suivi gain massique</div>
              </div>
              <div style={S.filters}>
                <label style={S.filterLabel}>
                  Granularité
                  <select style={S.select} value={gran} onChange={(e) => setGran(e.target.value)}>
                    <option value="mois">Mois</option>
                    <option value="semaine">Semaine</option>
                    <option value="jour">Jour</option>
                  </select>
                </label>
                <label style={S.filterLabel}>
                  Population
                  <select style={S.select} value={popMode} onChange={(e) => setPopMode(e.target.value)}>
                    <option value="melange">Tout (mélangé)</option>
                    {GAIN_MASSIQUE_POP_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div style={{ color: COLORS.gray, fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
              {GRAN_LABELS[gran]} — {POP_LABELS[popMode]} · {nPoints} stack(s) affiché(s)
            </div>

            <div id="gain-massique-chart-wrap" style={{ width: "100%", overflowX: "auto" }}>
              {series.length === 0 || nPoints === 0 ? (
                <div style={{ color: COLORS.gray, padding: 32, textAlign: "center" }}>
                  Aucune donnée pour cette sélection.
                </div>
              ) : (
                <BoxplotSvg
                  series={series}
                  color={color}
                  gran={gran}
                  width={Math.max(chartWidth, series.length * 100)}
                  height={540}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const S = {
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    marginBottom: 16,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 4,
  },
  filters: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  filterLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solidrgb(183, 229, 32)",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(183, 229, 32, 1)",
    fontSize: 13,
    minWidth: 170,
  },
};
